import { IS_GET_SETTINGS_SUPPORTED } from '../common/rtc-detection';
import { ChangeState, FSM, FSMError } from 'afsm';
import { ILocalTrack, IRoom, LocalTrackEvent, MediaType } from '../enums';
import { MediaTypes, PlayOptions, PlayerClassType, PlayerType, Track, TrackType } from './track';
import RtcError from '../common/rtc-error';
import { getDeviceModel, getOSNumber, getOSString, IOS_MAIN_VERSION, IS_ANDROID, IS_IOS, IS_MAC, IS_SAFARI } from '../utils/environment';
import { NAME, RECOVER_CAPTURE_INTERVAL } from '../common/constants';
import innerEmitter from '../inner-emitter/inner-emitter';
import INNER_EVENT, { InnerEventTypes } from '../inner-emitter/inner-event';
import getUserMedia from '../common/capture/get-user-media';
import { DeviceInfo, getCameras, getDevices, getMicrophones, isCameraTrack, isMicrophoneTrack, isSameDevice } from '../common/device-detector';
import { BaseCaptureOption, CaptureOption, checkPendingPromise, ErrorCode, FacingMode, LocalAudioTrack, LocalVideoTrack, Logger, RoomEvent, TRACK_KIND } from '..';
import { createDecorator } from '../utils/decorators/middleware';
import { delay, getMuteStateFromFlag, performanceNow } from '../utils/utils';
import { logVisibilityState } from '../utils/log-visibility-state-changed';
import { addCallQueue } from '../utils/decorators/api-call-queue';
import kvStatManager, { KV_REPORT_KEY_AUDIO_ENCODE, KV_REPORT_KEY_VIDEO_ENCODE, KV_REPORT_KEY_NETWORK } from '../manager/kv-stat-manager';
import eventLog from '../utils/log/event-log';
import { reportSuccessRate } from '../utils/decorators/report-success-rate';
import { filter, fromEvent, merge, pipe, subscribe, takeUntil } from 'fastrx';
export const enum LocalTrackState {
  READY = 'ready',
  PUBLISH = 'publish'
}

export enum LocalTrackPublishState {
  STARTED = 'started',
  STOPPED = 'stopped',
  STARTING = 'starting'
}

export enum PublishStateStoppedReason {
  TIMEOUT = 'timeout',
  ERROR = 'error',
  API_CALL = 'api-call'
}

export enum RecaptureMode { AUTO_SWITCH_NEW_DEVICE, WAIT_CURRENT_DEVICE }

export interface LocalTrackEventTypes {
  [LocalTrackEvent.PUBLISH_STATE_CHANGED]: [{
    mediaType: 'audio' | 'video' | 'screen',
    state: LocalTrackPublishState,
    prevState: LocalTrackPublishState,
    // 状态变为 stopped 的原因
    // timeout 连接超时 ----> 业务侧无需处理，可以提示给用户检查网络
    // error 服务器错误，拒绝推流，例如不支持 h264 编码的设备上，并且没配置 vp8 编码白名单时，尝试推流 -----> 业务侧需要处理，例如申请 vp8 白名单
    // api_call 业务侧 api 调用导致，例如：推流重试过程中，业务侧调用 stopLocalVideo, exitRoom 等 api 停止推流，会停止重试。 ----> 业务侧无需处理
    reason?: PublishStateStoppedReason,
    error?: RtcError;
  }];
  [LocalTrackEvent.DEVICE_AUTO_RECOVERED]: [{ deviceId: string; }],
  [LocalTrackEvent.DEVICE_CHANGED]: [device: DeviceInfo],
  [LocalTrackEvent.DEVICE_RECOVER_FAILED]: [RtcError | Error],
  [LocalTrackState.READY]: [],
  [LocalTrackState.PUBLISH]: [],
  [LocalTrackEvent.ENCODE_FAILED]: [ILocalTrack],
  [LocalTrackEvent.TRACK_ENDED]: [],
  [FSM.INIT]: [],
  [LocalTrackEvent.RENDER]: [source: CanvasImageSource];
}

// 单个Track的推流状态机
export abstract class LocalTrack<T extends AudioOrVideo> extends Track<T, LocalTrackEventTypes> implements ILocalTrack {
  isRemote = false;
  deviceId?: string;
  groupId = '';
  label = '';
  sourceTrack?: PlayOptions<T>['track']; // 采集的原始track, 摄像头和麦克风原始属性更新作用在此. ref: https://tapd.woa.com/tapd_fe/20396022/bug/detail/1020396022141757527

  enableAutoSwitchWhenRecapturing = true;
  private _isRecapturing = false;
  private _lastRecaptureTime = 0;
  private _onMuteTimeoutId = -1;
  private _encodeCheckTimeoutId = -1;
  recaptureMode = RecaptureMode.AUTO_SWITCH_NEW_DEVICE;

  abstract stat: LocalAudioStat | LocalVideoStat;
  get enableEncodeFrame() {
    return false;
  }
  profile: T extends 'audio' ? AudioProfile : VideoProfile;
  abstract listenDeviceChange(): void;
  constructor(mediaType: MediaTypes<T>, PlayerClass: PlayerClassType<T>, room?: IRoom) {
    super({ mediaType, PlayerClass, room });
  }
  get isPublishing() {
    return this.state.toString() === 'publishing';
  }
  get isPublished() {
    return this.state === LocalTrackState.PUBLISH;
  }
  get isUseCustomSource() {
    return !!(this.mediaTrack && this.sourceTrack !== this.mediaTrack);
  }
  encodeFrame(data: RTCEncodedAudioFrame | RTCEncodedVideoFrame, small?: boolean): RTCEncodedAudioFrame | RTCEncodedVideoFrame {
    throw new Error('Method not implemented.');
  }
  installTrackEvent(track: MediaStreamTrack) {
    track.addEventListener(NAME.MUTE, this.onTrackMuted);
    track.addEventListener(NAME.UNMUTE, this.onTrackUnmuted);
    track.addEventListener(NAME.ENDED, this.onTrackEnded);
    if (track.muted) {
      this.onTrackMuted();
    }
    if (track.readyState === NAME.ENDED) {
      this.onTrackEnded();
    }
  }

  uninstallTrackEvent(track: MediaStreamTrack) {
    track.removeEventListener(NAME.MUTE, this.onTrackMuted);
    track.removeEventListener(NAME.UNMUTE, this.onTrackUnmuted);
    track.removeEventListener(NAME.ENDED, this.onTrackEnded);
  }

  @ChangeState(FSM.INIT, LocalTrackState.READY, { ignoreError: true, sync: true })
  setStateToReady() { }

  @addCallQueue()
  async capture(captureOption: BaseCaptureOption | CaptureOption, stopAfterCaptured = false) {
    const prevSourceTrack = this.sourceTrack;
    try {
      const captureStartTime = performanceNow();
      innerEmitter.emit(INNER_EVENT.LOCAL_TRACK_CAPTURE_START, { track: this });
      let mediaStream;
      if (captureOption.customSource) {
        mediaStream = new MediaStream();
        mediaStream.addTrack(captureOption.customSource);
      } else {
        if (!stopAfterCaptured) {
          this.sourceTrack?.stop();
        }
        mediaStream = await getUserMedia(captureOption as BaseCaptureOption);
      }
      const track = mediaStream.getTracks()[0];
      await this.setInputMediaStreamTrack(track as TrackType<T>);
      if (!captureOption.customSource) {
        this.sourceTrack = track as TrackType<T>;
        this.updateDeviceIdInUse();
        this.listenDeviceChange();
      }
      innerEmitter.emit(INNER_EVENT.LOCAL_TRACK_CAPTURE_SUCCESS, { track: this, cost: performanceNow() - captureStartTime, profile: this.profile, room: this.manager?.room });
      return mediaStream;
    } catch (error) {
      innerEmitter.emit(INNER_EVENT.LOCAL_TRACK_CAPTURE_FAILED, { track: this, error });
      this.log.error(`getUserMedia error observed ${error}`);
      throw error;
    } finally {
      if (stopAfterCaptured) prevSourceTrack?.stop();
    }
  }

  setOutputMediaStreamTrack(track: TrackType<T>): void | Promise<void> {
    super.setOutputMediaStreamTrack(track);
    this.setStateToReady();
    if (this.isPublishing || this.isPublished) return this.room?.replaceTrack(this);
  }

  get hasFlag() {
    const state = getMuteStateFromFlag(this.room?.localPublishFlag || 0, this.room?.userId || '');
    return (this.mediaType === MediaType.BIG_VIDEO && state.hasVideo) || (this.mediaType === MediaType.AUDIO && state.hasAudio) || (this.mediaType === MediaType.AUX_VIDEO && state.hasAuxiliary);
  }

  @ChangeState(LocalTrackState.READY, LocalTrackState.PUBLISH, {
    ignoreError: true,
    success(this: LocalTrack<T>) {
      innerEmitter.emit(INNER_EVENT.LOCAL_TRACK_PUBLISHED, { track: this, room: this.room! });
      this.emit(LocalTrackEvent.PUBLISH_STATE_CHANGED, { mediaType: this.strMediaType, state: LocalTrackPublishState.STARTED, prevState: LocalTrackPublishState.STARTING });
      this.log.info('published');
    }, fail(this: LocalTrack<T>, err: FSMError | RtcError) {
      this.room?.localTracks.delete(this);
      let reason: PublishStateStoppedReason = PublishStateStoppedReason.ERROR;
      const error = err instanceof RtcError ? err : err.cause instanceof RtcError ? err.cause : err;
      let isAborted = false;
      if (error instanceof RtcError) {
        if (error.message.includes('timeout')) {
          reason = PublishStateStoppedReason.TIMEOUT;
        } else if (error.code === ErrorCode.API_CALL_ABORTED) {
          isAborted = true;
          reason = PublishStateStoppedReason.API_CALL;
        }
      }
      this.emit(LocalTrackEvent.PUBLISH_STATE_CHANGED, {
        mediaType: this.strMediaType,
        state: LocalTrackPublishState.STOPPED,
        prevState: LocalTrackPublishState.STARTING,
        reason,
        error: error as RtcError
      });
      this.log[isAborted ? 'info' : 'error']('publish failed', error);
    }
  })
  @reportSuccessRate(KV_REPORT_KEY_NETWORK.PUBLISH, false)
  async publish(room: IRoom, roomPublishPromise: Promise<any>) {
    this.room = room;
    this.room.localTracks.add(this);
    this.emit(LocalTrackEvent.PUBLISH_STATE_CHANGED, { mediaType: this.strMediaType, state: LocalTrackPublishState.STARTING, prevState: LocalTrackPublishState.STOPPED });
    this.userId = room.userId;
    this._log.bindParent(room.getLogger());
    await roomPublishPromise;
    return this._checkPublishFlag(room);
  }

  private _checkPublishFlag(room: IRoom) {
    return new Promise<void>(async (resolve, reject) => {
      const abort = () => reject(new RtcError({ code: ErrorCode.API_CALL_ABORTED, message: 'publish aborted' }));

      // 已经有 flag || 上行处于 muted 状态，则无需等待服务端 flag 变更。
      if (this.hasFlag || this.muted) {
        resolve();
      } else {
        if (this.state === FSM.INIT || this.state === LocalTrackState.READY) abort();
        pipe(fromEvent(room, RoomEvent.LOCAL_PUBLISH_FLAG_CHANGED), filter(() => this.hasFlag), takeUntil(merge(fromEvent(this, FSM.INIT), fromEvent(this, LocalTrackState.READY))), subscribe(resolve, reject, abort));
      }
      // 网络质量不好，可能会影响编码成功率判断，此时忽略
      if (this.room?.networkQuality?.hadRecentBadUplink?.(2)) return resolve();
      const hearbeatCount = room.heartbeatCount;
      const prevTotalFrames = this.mediaTrack?.stats?.totalFrames || 0;
      this._encodeCheckTimeoutId = setTimeout(async () => {
        // 网络质量不好，可能会影响编码成功率判断，此时忽略
        // 心跳次数不及 3 次，主线程阻塞，影响 stat 更新，此时忽略。
        if (this.room?.networkQuality?.hadRecentBadUplink?.(2) || room.heartbeatCount - hearbeatCount < 3) return resolve();
        if ((this.isPublished || this.isPublishing)) {
          const isAudio = this.kind === NAME.AUDIO;
          if (this.mediaTrack?.stats) {
            const totalFrames = this.mediaTrack.stats.totalFrames || 0;
            if (totalFrames - prevTotalFrames === 0) this.log.warn('capture totalFrames is 0 during encode check, totalFrames', totalFrames);
          }
          if (!this.isMediaTrackActive) {
            this.emit(LocalTrackEvent.ENCODE_FAILED, this);
            return reject(new RtcError({ message: `${this.strMediaType} encode failed, track is not active`, code: isAudio ? ErrorCode.AUDIO_ENCODE_FAILED : ErrorCode.VIDEO_ENCODE_FAILED }));
          }

          const isEncodedSuccess = this.stat.bytesSent > 0;
          kvStatManager[isEncodedSuccess ? 'addSuccessEvent' : 'addFailedEvent']({
            key: isAudio ? KV_REPORT_KEY_AUDIO_ENCODE.ENCODE_RESULT : KV_REPORT_KEY_VIDEO_ENCODE.ENCODE_RESULT
          });
          if (!isAudio) {
            const codec = this.room?.videoCodec?.toUpperCase() || 'H264';
            const encodeResultKey = {
              H264: KV_REPORT_KEY_VIDEO_ENCODE.ENCODE_H264_RESULT,
              H265: KV_REPORT_KEY_VIDEO_ENCODE.ENCODE_H265_RESULT,
              VP8: KV_REPORT_KEY_VIDEO_ENCODE.ENCODE_VP8_RESULT
            }[codec];
            if (encodeResultKey) kvStatManager[isEncodedSuccess ? 'addSuccessEvent' : 'addFailedEvent']({
              key: encodeResultKey
            });
          }
          if (!isEncodedSuccess) {
            kvStatManager.addEnum({ key: isAudio ? KV_REPORT_KEY_AUDIO_ENCODE.ENCODE_FAILED_OS : KV_REPORT_KEY_VIDEO_ENCODE.ENCODE_FAILED_OS, value: getOSNumber() });
            eventLog.uploadEvent({ log: `stat-encode-failed-${this.kind}-${getDeviceModel() || getOSString()}`, userId: this.userId });
            this.log.warn(isAudio ? 'encode failed' : `${this.room?.videoCodec?.toUpperCase()} encode failed`);

            // 如果编码失败，尝试重试，重试完成后还没编码成功则 reject & 抛事件
            if (this.retryEncodeFailed) {
              this.log.warn('retry encode');
              await this.retryEncodeFailed(this);
              if (this.stat.bytesSent > 0 || this.hasFlag) return resolve();
              await delay(5000);
              if (this.stat.bytesSent > 0 || this.hasFlag) return resolve();
            }
            this.emit(LocalTrackEvent.ENCODE_FAILED, this);
            reject(new RtcError({ message: `${this.strMediaType} encode failed`, code: isAudio ? ErrorCode.AUDIO_ENCODE_FAILED : ErrorCode.VIDEO_ENCODE_FAILED }));
          }
        }
      }, 10000);
    });
  }

  retryEncodeFailed?: (...args: any[]) => Promise<void>;

  @createDecorator(next => async function <THIS extends LocalTrack<T>> (this: THIS) {
    const prevState = this.state === LocalTrackState.PUBLISH ? LocalTrackPublishState.STARTED : LocalTrackPublishState.STARTING;
    next.call(this);
    this.emit(LocalTrackEvent.PUBLISH_STATE_CHANGED, {
      mediaType: this.strMediaType,
      state: LocalTrackPublishState.STOPPED,
      prevState,
      reason: PublishStateStoppedReason.API_CALL
    });
    clearTimeout(this._encodeCheckTimeoutId);
  })
  @ChangeState([], LocalTrackState.READY, { sync: true })
  unpublish() {
    if (this.room) this.room.localTracks.delete(this);
    this.log.info('unpublish');
    innerEmitter.emit(INNER_EVENT.LOCAL_TRACK_UNPUBLISHED, { track: this });
  }

  async updateDeviceIdInUse() {
    if (this.sourceTrack && IS_GET_SETTINGS_SUPPORTED) {
      const { deviceId, groupId } = this.sourceTrack.getSettings();
      const { label } = this.sourceTrack;
      const isSame = await isSameDevice({ newDeviceId: deviceId, oldDeviceId: this.deviceId, oldGroupId: this.groupId, oldLabel: this.label, kind: this.kind });
      if (!isSame) {
        this.deviceId = deviceId;
        this.label = label;
        if (groupId) this.groupId = groupId;
        getDevices().then(devices => {
          const device = devices.find(device => {
            let result = device.deviceId === deviceId;
            if (groupId) result = result && device.groupId === groupId;
            return result;
          });
          if (device) {
            this.emit(LocalTrackEvent.DEVICE_CHANGED, device);
          }
        });
      }
      // TODO: 混音、美颜情况下的 deviceId 更新。
    }
  }

  setProfile(profile: VideoProfile | AudioProfile) {
    this.log.info('setProfile', profile);
    Object.assign(this.profile, profile);
  }

  isNeedToRecapture(isTrackMuted = false) {
    if (
      // 非摄像头、麦克风不恢复
      !this.deviceId || !this.sourceTrack ||
      (this.kind === NAME.AUDIO && !isMicrophoneTrack(this.sourceTrack)) ||
      (this.kind === NAME.VIDEO && !isCameraTrack(this.sourceTrack)) ||
      // 正在恢复中
      this._isRecapturing ||
      // 1. Mac Safari mute 不恢复采集
      // 2. iOS mute 恢复采集
      // 原因：https://tapd.woa.com/TerWebG/prong/stories/view/1020396022884881239
      (isTrackMuted && IS_MAC && IS_SAFARI)) {
      return false;
    }
    return true;
  }

  onTrackMuted() {
    super.onTrackMuted();
    logVisibilityState();
    if (!this.isNeedToRecapture(true)) {
      return;
    }
    // 确保两次恢复采集的时间间隔大于 2s。避免在 iOS 接电话时，SDK 出现频繁恢复采集的问题
    if (Date.now() - this._lastRecaptureTime < RECOVER_CAPTURE_INTERVAL) {
      setTimeout(() => this.onTrackMuted(), RECOVER_CAPTURE_INTERVAL);
      return;
    }
    this._onMuteTimeoutId = setTimeout(async () => {
      if (this.sourceTrack?.muted) {
        if ((IS_IOS || IS_ANDROID) && document.visibilityState !== 'visible') return;
        this.recapture((await this.getRecoverCaptureDeviceId())!);
      }
    }, 5000);
  }

  onTrackUnmuted() {
    super.onTrackUnmuted();
    if (this._onMuteTimeoutId > 0) {
      clearTimeout(this._onMuteTimeoutId);
    }
  }
  async onTrackEnded() {
    super.onTrackEnded();
    if (!this.isNeedToRecapture()) return;
    if (this.recaptureMode === RecaptureMode.AUTO_SWITCH_NEW_DEVICE) {
      // 确保两次恢复采集的时间间隔大于 2s。避免在 iOS 接电话时，SDK 出现频繁恢复采集的问题
      if (Date.now() - this._lastRecaptureTime < RECOVER_CAPTURE_INTERVAL) {
        setTimeout(() => this.onTrackEnded(), RECOVER_CAPTURE_INTERVAL);
        return;
      }
      this.emit(LocalTrackEvent.TRACK_ENDED);
      this.recapture((await this.getRecoverCaptureDeviceId())!);
    }
  }

  /**
   * 恢复采集
   */
  async recapture(deviceId: string | FacingMode, stopAfterCaptured = false) {
    if (this._isRecapturing || !this.sourceTrack) return;
    this.log.warn('recapture trying');
    const prevSourceTrack = this.sourceTrack;
    if (!stopAfterCaptured) {
      this.sourceTrack?.stop();
    }
    this._isRecapturing = true;
    this._lastRecaptureTime = Date.now();

    const captureOption: CaptureOption = { useExactDeviceId: true };
    if (deviceId === FacingMode.user || deviceId === FacingMode.environment) {
      captureOption.facingMode = deviceId;
    } else {
      let deviceIdToCapture = undefined;
      const deviceList = this.kind === TRACK_KIND.AUDIO ? await getMicrophones() : await getCameras();
      if (deviceList.find(item => item.deviceId === deviceId)) {
        deviceIdToCapture = deviceId;
      }
      captureOption.deviceId = deviceIdToCapture;
    }
    return this.capture(captureOption, stopAfterCaptured)
      .then(() => {
        this._isRecapturing = false;
        this.log.warn('recapture success');
        this.emit(LocalTrackEvent.DEVICE_AUTO_RECOVERED, {
          deviceId: this.deviceId!
        });
        innerEmitter.emit(INNER_EVENT.LOCAL_TRACK_RECAPTURE, { track: this });

        if (this.sourceTrack?.muted) {
          this.log.warn('track still muted after recapture');
          this.onTrackMuted();
        }
      })
      .catch((error: Error) => {
        this._isRecapturing = false;
        this.log.warn(`recapture failed ${error.message}`);
        this.emit(LocalTrackEvent.DEVICE_RECOVER_FAILED, error);
        innerEmitter.emit(INNER_EVENT.LOCAL_TRACK_RECAPTURE, { track: this, error });
      })
      .finally(() => {
        if (stopAfterCaptured) prevSourceTrack?.stop();
      });
  }

  async getRecoverCaptureDeviceId() {
    const IS_VIDEO_TRACK = this instanceof LocalVideoTrack;
    if (IS_VIDEO_TRACK && this.facingMode) {
      return this.facingMode;
    }
    let { deviceId } = this;
    if (deviceId) {
      // 同一个 deviceId 经过恢复采集后，仍然采集异常（出现3次），则尝试更换设备恢复采集。
      const captureFailCount = (captureFailDeviceIdCountMap.get(deviceId) || 0) + 1;
      captureFailDeviceIdCountMap.set(deviceId, captureFailCount);
      if (captureFailCount >= 3 && this.enableAutoSwitchWhenRecapturing) {
        const device = IS_VIDEO_TRACK
          ? (await getCameras()).find(camera => !captureFailDeviceIdCountMap.has(camera.deviceId))
          : (await getMicrophones()).find(microphone => !captureFailDeviceIdCountMap.has(microphone.deviceId));
        if (device) {
          this.log.warn(`${deviceId} capture fail ${captureFailCount} times, change new ${device.deviceId}`);
          deviceId = device.deviceId;
        }
      }
    }
    return deviceId;
  }

  stopCapture() {
    if (this.sourceTrack) {
      this.sourceTrack.stop();
      innerEmitter.emit(INNER_EVENT.LOCAL_TRACK_STOPPED, { track: this });
      this.uninstallTrackEvent(this.sourceTrack);
    }
    if (this._inputTrack) {
      this.uninstallTrackEvent(this._inputTrack);
    }
    this.manager?.removeInput(this);
    if (this._onMuteTimeoutId) {
      clearTimeout(this._onMuteTimeoutId);
    }
  }

  close(): void {
    super.close();
    this.stopCapture();
  }
}

// 记录设备采集失败的次数，若经过 sdk 恢复采集后，仍然无法正常采集（异常3次+），则尝试换设备采集
export const captureFailDeviceIdCountMap: Map<string, number> = new Map();

// 切换设备后，清理 captureFailDeviceIdCountMap 记录。
innerEmitter.on(INNER_EVENT.SWITCH_DEVICE_SUCCESS, (event: InnerEventTypes[INNER_EVENT.SWITCH_DEVICE_SUCCESS][0]) => {
  if (event.track.deviceId) {
    captureFailDeviceIdCountMap.delete(event.track.deviceId);
  }
});
